import { forwardRef, useMemo } from "react";
import {
  Ban, BookOpen, Heart, Home, MessageCircle, Moon, ShieldCheck,
  Smartphone, Star, UsersRound,
} from "lucide-react";
import "./room-sheet.css";

const RULES = [
  { icon: ShieldCheck, text: "Respeta a tus líderes, compañeros y las instalaciones del campamento." },
  { icon: BookOpen, text: "Participa activamente en los servicios, devocionales y actividades programadas." },
  { icon: MessageCircle, text: "Mantén un lenguaje y comportamiento apropiado para un ambiente cristiano." },
  { icon: Ban, text: "No salir del área del campamento sin permiso de un líder responsable." },
  { icon: UsersRound, text: "Los varones y las chicas deben permanecer en sus áreas asignadas para dormir." },
  { icon: Smartphone, text: "Uso del celular con moderación: prioriza estar presente en cada momento del encuentro." },
  { icon: Moon, text: "Cuida el descanso: respeta los horarios de silencio nocturno." },
  { icon: Heart, text: "Ven con actitud de servicio y disposición a dejarte transformar por Dios." },
];

const formatPhone = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  const local = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (local.length === 10) return `${local.slice(0, 3)}-${local.slice(3, 6)}-${local.slice(6)}`;
  return value || "Sin teléfono";
};

const memberLayout = (count) => {
  if (count > 60) return { columns: 5, density: "max" };
  if (count > 36) return { columns: 4, density: "very" };
  if (count > 20) return { columns: 3, density: "dense" };
  if (count > 10) return { columns: 2, density: "comfortable" };
  return { columns: 1, density: "open" };
};

export const RoomSheet = forwardRef(({ room }, ref) => {
  const leaderIds = useMemo(() => new Set((room.leaders || []).map((leader) => leader.id)), [room.leaders]);
  const displayMembers = useMemo(
    () => (room.members || []).filter((member) => !leaderIds.has(member.id)),
    [room.members, leaderIds],
  );
  const layout = memberLayout(displayMembers.length);
  const rows = Math.max(Math.ceil(displayMembers.length / layout.columns), 1);
  const longTitle = room.name.length > 22 ? "room-sheet--long-title" : "";

  return (
    <article
      ref={ref}
      className={`room-sheet room-sheet--${layout.density} ${longTitle}`}
      style={{ "--room-color": room.color || "#fbbf24", "--member-columns": layout.columns, "--member-rows": rows }}
      data-testid={`room-sheet-${room.kind}-${room.id}`}
    >
      <div className="room-sheet__texture" aria-hidden="true" />

      <header className="room-sheet__header">
        <div className="room-sheet__logo-badge">
          <img src="/api/assets/rescate-logo.png" alt="Logo Operación Rescate" className="room-sheet__logo" />
          <div className="room-sheet__date">28–30 DE AGOSTO 2026</div>
        </div>
        <div className="room-sheet__header-copy">
          <div className="room-sheet__mountain-line" aria-hidden="true" />
          <div className="room-sheet__kind-label"><Home size={22} /> {room.kind_label}</div>
          <div className="room-sheet__brand">OPERACIÓN RESCATE</div>
          <div className="room-sheet__subbrand">CAMPAMENTO JUVENIL</div>
        </div>
      </header>

      <section className="room-sheet__identity">
        <span className="room-sheet__color-block" aria-label={`Color ${room.color || "dorado"}`} />
        <h1 data-testid={`sheet-room-name-${room.id}`}>{room.name}</h1>
      </section>

      <section className="room-sheet__summary" aria-label="Resumen de alojamiento">
        <SummaryItem icon={UsersRound} label="Capacidad" value={room.capacity} id={`sheet-capacity-${room.id}`} />
        <SummaryItem icon={UsersRound} label="Ocupantes" value={displayMembers.length} id={`sheet-occupants-${room.id}`} />
        <SummaryItem icon={Star} label="Líderes" value={room.leaders.length} id={`sheet-leaders-count-${room.id}`} />
      </section>

      <section className="room-sheet__section room-sheet__leaders">
        <SectionTitle title="LÍDERES" tone="pink" />
        <div className="room-sheet__leader-head"><span>NOMBRE</span><span>TELÉFONO</span></div>
        <div className="room-sheet__leader-list">
          {room.leaders.length === 0 ? (
            <div className="room-sheet__empty">Sin líderes asignados</div>
          ) : room.leaders.map((leader) => (
            <div className="room-sheet__leader-row" key={leader.id} data-testid={`sheet-leader-${leader.id}`}>
              <strong>{leader.full_name}</strong>
              <span data-testid={`sheet-leader-phone-${leader.id}`}>{formatPhone(leader.phone)}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="room-sheet__section room-sheet__members">
        <SectionTitle title="INTEGRANTES" tone="gold" />
        <div className="room-sheet__member-head">NOMBRE COMPLETO</div>
        <div className="room-sheet__member-grid">
          {displayMembers.length === 0 ? (
            <div className="room-sheet__empty">Sin integrantes asignados</div>
          ) : displayMembers.map((member) => (
            <div className="room-sheet__member-row" key={member.id} data-testid={`sheet-member-${member.id}`}>
              {member.full_name}
            </div>
          ))}
        </div>
      </section>

      <section className="room-sheet__rules">
        <div className="room-sheet__rules-title">REGLAS <span>DEL CAMPAMENTO</span></div>
        <div className="room-sheet__rules-grid">
          {RULES.map(({ icon: Icon, text }, index) => (
            <div className="room-sheet__rule" key={text}>
              <div className="room-sheet__rule-icon"><Icon size={17} /></div>
              <b>{index + 1}</b>
              <p>{text}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="room-sheet__footer">
        <img src="/api/assets/church-logo.png" alt="Logo Primera Iglesia del Nazareno Casa de Oración Ven y Ve" className="room-sheet__church-logo" />
        <div className="room-sheet__motto">
          <em>Juntos</em><span>RESCATADOS PARA</span><strong>TRANSFORMAR</strong>
        </div>
        <div className="room-sheet__footer-bar">OPERACIÓN RESCATE <i /> CAMPAMENTO JUVENIL 2026</div>
      </footer>
    </article>
  );
});

RoomSheet.displayName = "RoomSheet";

const SummaryItem = ({ icon: Icon, label, value, id }) => (
  <div className="room-sheet__summary-item">
    <Icon size={21} />
    <span>{label}:</span>
    <b data-testid={id}>{value}</b>
  </div>
);

const SectionTitle = ({ title, tone }) => (
  <div className={`room-sheet__section-title room-sheet__section-title--${tone}`}>
    <span>{title}</span><UsersRound size={20} />
  </div>
);